/**
 * 漁獲調査 API（/api/fishing-grounds, /api/catch-csv。担当: G4）
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface FishingGroundListItem {
  id: number;
  fiscalYear: number;
  businessOperatorId: number;
  businessOperatorName: string;
  vesselId: number;
  vesselName: string;
  registrationNo: string;
  /** 船舶トン数（参加船名簿） */
  tonnage: number | null;
  ownerName: string | null;
  fishingMethod: string | null;
  gear: string | null;
  voyageFrom: string;
  voyageTo: string;
  reportSubmittedOn: string | null;
  reporterName: string | null;
  landingDate: string | null;
  landingTotalInclTax: number | null;
  entryCount: number;
  updatedAt: string;
}

export interface FishingGroundEntry {
  id?: number;
  surveyDate: string;
  landingPort: string | null;
  zoneNo: number | null;
  zoneNoCalculated?: number | null;
  latDeg: number | null;
  latMin: number | null;
  latSec: number | null;
  lonDeg: number | null;
  lonMin: number | null;
  lonSec: number | null;
  operationCount: number | null;
  weather: string | null;
  windDirection: string | null;
  windForce: string | null;
  waterTemp: number | null;
  depth: number | null;
  current: string | null;
  sonarReaction: number | null;
  schoolCondition: string | null;
  fishSpeciesId: number | null;
  standardName: string | null;
  localName: string | null;
  count: number | null;
  weight: number | null;
  note: string | null;
}

export interface FishingGroundHeader {
  fiscalYear: number;
  businessOperatorId: number | null;
  vesselId: number | null;
  ownerName: string | null;
  fishingMethod: string | null;
  gear: string | null;
  gearCount: number | null;
  voyageFrom: string;
  voyageTo: string;
  reportSubmittedOn: string | null;
  reporterName: string | null;
  landingDate: string | null;
  fishingGround: string | null;
  landingTotalInclTax: number | null;
  shippingDeductionInclTax: number | null;
  netAmountExclTax: number | null;
}

export interface FishingGroundDetail extends FishingGroundHeader {
  id: number;
  businessOperatorId: number;
  vesselId: number;
  vesselName: string;
  registrationNo: string;
  businessOperatorName: string;
  entries: (FishingGroundEntry & { id: number; zoneNoCalculated: number | null })[];
  updatedAt: string;
  warnings?: { rowIndex?: number; path?: string; message: string }[];
}

export type FishingGroundInput = FishingGroundHeader & { entries: FishingGroundEntry[] };

export interface FishingGroundFilter {
  businessOperatorId?: number | null;
  registrationNo?: string;
  vesselName?: string;
  voyageFrom?: string;
  voyageTo?: string;
  fishingMethod?: string;
}

export interface ReferenceItem {
  id: number;
  ownerName: string | null;
  departureDate: string | null;
  arrivalDate: string | null;
  landingDate: string;
  fishingMethodName: string | null;
  speciesTypeName: string | null;
  speciesName: string | null;
  count: number | null;
  unitPrice: number | null;
  amount: number | null;
}

export interface CsvImportResult {
  imported: number;
  updated: number;
  skipped: number;
  errors: { line: number; message: string }[];
  encoding: string;
  fiscalYears: number[];
}

export interface FishSpeciesDto {
  id: number;
  code: string;
  standardName: string;
  localName: string | null;
  typeCode: string | null;
}

export const catchKeys = {
  all: ['catch'] as const,
  list: (year: number, f: FishingGroundFilter) => [...catchKeys.all, 'list', year, f] as const,
  detail: (id: number) => [...catchKeys.all, 'detail', id] as const,
  reference: (year: number, op: number, from: string, to: string) => [...catchKeys.all, 'reference', year, op, from, to] as const,
  species: ['catch', 'fish-species'] as const,
  captains: (vesselId: number, from: string, to: string) => [...catchKeys.all, 'captains', vesselId, from, to] as const,
};

export interface CaptainItem {
  periodFrom: string;
  periodTo: string;
  captainName: string;
}

/** 船長名（速報から取得）。参加船と調査航海期間が揃ったときだけ取得する */
export function useCaptains(vesselId: number | null, from: string, to: string) {
  const enabled = vesselId != null && /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && from <= to;
  return useQuery({
    queryKey: catchKeys.captains(vesselId ?? 0, from, to),
    queryFn: ({ signal }) => api.get<{ items: CaptainItem[] }>('/fishing-grounds/captains', { vesselId, voyageFrom: from, voyageTo: to }, { signal }),
    enabled,
  });
}

export function useFishingGrounds(year: number, f: FishingGroundFilter) {
  return useQuery({
    queryKey: catchKeys.list(year, f),
    queryFn: ({ signal }) => api.get<{ items: FishingGroundListItem[]; total: number }>('/fishing-grounds', { fiscalYear: year, ...f }, { signal }),
    placeholderData: keepPreviousData,
  });
}

export function useFishingGround(id: number | null) {
  return useQuery({
    queryKey: catchKeys.detail(id ?? 0),
    queryFn: ({ signal }) => api.get<FishingGroundDetail>(`/fishing-grounds/${id}`, undefined, { signal }),
    enabled: id != null,
  });
}

export function useReference(year: number, businessOperatorId: number | null, from: string, to: string) {
  const enabled = businessOperatorId != null && /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && from <= to;
  return useQuery({
    queryKey: catchKeys.reference(year, businessOperatorId ?? 0, from, to),
    queryFn: ({ signal }) =>
      api.get<{ items: ReferenceItem[] }>('/fishing-grounds/reference', { fiscalYear: year, businessOperatorId, voyageFrom: from, voyageTo: to }, { signal }),
    enabled,
  });
}

export function useFishSpecies() {
  return useQuery({
    queryKey: catchKeys.species,
    queryFn: ({ signal }) => api.get<{ species: FishSpeciesDto[] }>('/masters/fish-species', undefined, { signal }),
    staleTime: 10 * 60_000,
  });
}

export function useSaveFishingGround(id: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FishingGroundInput) =>
      id == null ? api.post<FishingGroundDetail>('/fishing-grounds', input) : api.put<FishingGroundDetail>(`/fishing-grounds/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: catchKeys.all }),
  });
}

export function useDeleteFishingGround() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/fishing-grounds/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: catchKeys.all }),
  });
}

export function useImportCatchCsv() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, businessOperatorId }: { file: File; businessOperatorId: number | null }) => {
      const fd = new FormData();
      fd.append('file', file);
      if (businessOperatorId != null) fd.append('businessOperatorId', String(businessOperatorId));
      return api.post<CsvImportResult>('/catch-csv/import', fd);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: catchKeys.all }),
  });
}
